import { X } from "lucide-react";
import { useEffect, useState } from "react";

interface GeneratingBillsModalProps {
  open: boolean;
  onClose: () => void;
  consumerCount: number;
  onComplete: () => void;
  simulateProgress?: boolean;
}

export const GeneratingBillsModal = ({
  open,
  onClose,
  consumerCount,
  onComplete,
  simulateProgress = true,
}: GeneratingBillsModalProps) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!open || !simulateProgress) return;

    // Simulate progress
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          // Wait a moment before calling onComplete
          setTimeout(() => {
            onComplete();
          }, 500);
          return 100;
        }
        return prev + 10;
      });
    }, 300);

    return () => clearInterval(interval);
  }, [open, simulateProgress, onComplete]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl w-full max-w-md p-6 relative">
        {/* Header */}
        <div className="flex items-start justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Generate Bills
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Generating estimated bills for selected consumers
            </p>
          </div>
          <button
            onClick={onClose}
            className="hover:bg-gray-100 p-1 rounded transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Progress Content */}
        <div className="flex flex-col items-center py-8">
          {/* Spinner */}
          <div className="relative w-20 h-20 mb-6">
            <div className="absolute inset-0 border-4 border-gray-200 rounded-full"></div>
            <div className="absolute inset-0 border-4 border-blue-600 rounded-full border-t-transparent animate-spin"></div>
          </div>

          {/* Status Text */}
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Generating Bills...
          </h3>
          <p className="text-sm text-gray-600 mb-6">
            Processing {consumerCount} consumers
          </p>

          {/* Progress Bar */}
          <div className="w-full space-y-2">
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
                style={{ width: `${progress}%` }}
              />
            </div>
            <p className="text-sm text-gray-600 text-center">
              {progress}% Complete
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
